import {
  LayoutResponseType,
  registerFormInstance,
  unregisterFormInstance,
} from '@app-store'
import { useEffect, useMemo, useRef } from 'react'
import { v4 as uuid } from 'uuid'
import { MODE_CONSTANTS } from '../utils/constants'
import { useAppDispatch } from '../utils/agent.store'
import { FormBehaviorRunner } from './FormBehaviorRunner'
import { triggerFormBehaviorEvaluation } from './formBehaviorTrigger.registry'
import {
  registerFormAncestryGetValues,
  unregisterFormAncestryGetValues,
} from './formAncestry.registry'
import { HybridFormBehaviorScopeProps } from '../types/formBehavior.types'

export const hasHybridLayoutFormRules = (
  layout: LayoutResponseType | null | undefined
): boolean =>
  !!(layout?.uid && (layout.layout_data?.meta?.form_rules?.length ?? 0) > 0)

export const triggerHybridFormBehaviorHydrate = (layoutUid?: string): void => {
  if (layoutUid) {
    triggerFormBehaviorEvaluation(layoutUid, 'hydrate')
  }
}

export const HybridFormBehaviorScope = ({
  layout,
  formUniqueId,
  setValue,
  getValues,
  reset,
  mode,
  children,
}: HybridFormBehaviorScopeProps) => {
  const dispatch = useAppDispatch()
  const formInstanceId = useMemo(() => uuid(), [])
  const ancestryRegisteredRef = useRef(false)
  const layoutUid = layout?.uid
  const formRules = layout?.layout_data?.meta?.form_rules
  const hasFormRules = hasHybridLayoutFormRules(layout)

  if (layout?.entity_name) {
    registerFormAncestryGetValues(formInstanceId, getValues)
    if (!ancestryRegisteredRef.current) {
      dispatch(
        registerFormInstance({
          instanceId: formInstanceId,
          entityName: layout.entity_name,
          layoutUid: layout.uid,
        })
      )
      ancestryRegisteredRef.current = true
    }
  }

  useEffect(() => {
    if (!layout?.entity_name) return

    return () => {
      if (!ancestryRegisteredRef.current) return
      dispatch(unregisterFormInstance(formInstanceId))
      unregisterFormAncestryGetValues(formInstanceId)
      ancestryRegisteredRef.current = false
    }
  }, [dispatch, formInstanceId, layout?.entity_name])

  useEffect(() => {
    if (hasFormRules && layoutUid && mode === MODE_CONSTANTS.ADD) {
      triggerFormBehaviorEvaluation(layoutUid, 'init')
    }
  }, [])

  if (!hasFormRules || !formRules || !layoutUid) {
    return <>{children}</>
  }

  return (
    <FormBehaviorRunner
      layoutUid={layoutUid}
      rules={formRules}
      setValue={setValue}
      getValues={getValues}
      reset={reset}
      formInstanceId={formInstanceId}
      formUniqueId={formUniqueId}
      entityName={layout?.entity_name}
    >
      {children}
    </FormBehaviorRunner>
  )
}
